/**
 * /operations/loans/disbursement-queue — Session 3a PR 10.
 *
 * Approved-not-yet-disbursed LoanApplications awaiting HR-triggered
 * OFF-CYCLE disbursement. Rawatib-owned per Session 3a §4.9.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Rawatib narration)
 *   2. InlineAISuggestion cards (batch-disburse quick-win + Mutaba'aa
 *      Article 90 resequencing)
 *   3. TraceableValue wrapping approved_amount + days_waiting (≥3
 *      wrappers via approved_amount/days_waiting/approved_at cells)
 */

import { useQuery } from "@tanstack/react-query";
import { ChevronRight, ShieldCheck, Wallet } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import {
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../components/ui";
import {
  session3aApi,
  type LoanDisbursementItem,
} from "../lib/session3a-api";
import { useLegalEntityGate } from "../lib/legalEntity";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "./_session3aHelpers";

const SLA_DAYS = 7;

export function LoanDisbursementQueuePage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "overdue">("all");

  const list = useQuery({
    queryKey: ["loan-disbursement-queue", legalEntityId],
    queryFn: () =>
      session3aApi.listLoanDisbursementQueue(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: LoanDisbursementItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const overdue = items.filter((r) => r.days_waiting > SLA_DAYS);
    const sumAll = items.reduce((acc, r) => acc + r.approved_amount_sar, 0);
    const oldest = items.reduce<LoanDisbursementItem | null>(
      (acc, r) => (acc && acc.days_waiting > r.days_waiting ? acc : r),
      null,
    );
    return {
      total: items.length,
      overdue: overdue.length,
      sumAll,
      oldest,
    };
  }, [items]);

  const filtered = useMemo(
    () => (filter === "overdue" ? items.filter((r) => r.days_waiting > SLA_DAYS) : items),
    [items, filter],
  );

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t(
            "operations.loans_disbursement.title",
            "Loan Disbursement Queue",
          )}
          subtitle={t(
            "operations.loans_disbursement.subtitle",
            "Approved loans awaiting HR-triggered OFF-CYCLE disbursement. Rawatib batches the run; Mutaba'aa verifies Article 90 headroom.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setFilter("all")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "all"
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.loans_disbursement.filter.all", "All approved")}
            </button>
            <button
              type="button"
              onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                filter === "overdue"
                  ? "border-rose-300 bg-rose-50 text-rose-800"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t(
                "operations.loans_disbursement.filter.overdue",
                `> ${SLA_DAYS} days waiting`,
              )}
              {stats.overdue > 0 && (
                <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                  {stats.overdue}
                </span>
              )}
            </button>
          </div>
          <button
            type="button"
            disabled={selected.size === 0}
            onClick={() =>
              showAlertTrace(
                t(
                  "operations.loans_disbursement.batch_note",
                  `Disburse ${selected.size} selected via OFF-CYCLE batch run. Wiring lands with POST /api/loans/disbursement-queue/disburse.`,
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95 disabled:opacity-50"
          >
            <Wallet className="h-4 w-4" />
            {t(
              "operations.loans_disbursement.cta.disburse",
              `Disburse ${selected.size > 0 ? selected.size : "selected"}`,
            )}
          </button>
        </div>

        {stats.oldest && stats.overdue > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<Wallet className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "operations.loans_disbursement.suggestion.batch_headline",
              `Rawatib flagged: ${stats.overdue} disbursements have all signatures + bank details — batch-disburse takes one click.`,
            )}
            body={t(
              "operations.loans_disbursement.suggestion.batch_body",
              "Oldest waiting > SLA. Process the batch to clear the queue and start arrears clocks correctly.",
            )}
            primaryAction={{
              label: t(
                "operations.loans_disbursement.suggestion.select_overdue",
                "Select overdue",
              ),
              onClick: () => {
                setSelected(
                  new Set(items.filter((r) => r.days_waiting > SLA_DAYS).map((r) => r.application_id)),
                );
                setFilter("overdue");
              },
            }}
            suggestionKey={`disbursement-batch-${stats.overdue}`}
          />
        )}
        <InlineAISuggestion
          agent="mutabaa"
          icon={<ShieldCheck className="h-4 w-4" />}
          severity="info"
          headline={t(
            "operations.loans_disbursement.suggestion.veto_headline",
            "Mutaba'aa veto preview.",
          )}
          body={t(
            "operations.loans_disbursement.suggestion.veto_body",
            "Any disbursement that would breach Article 90 (1/3 wage cap) when combined with existing loans is auto-vetoed. Resequencing suggested for borderline cases.",
          )}
          primaryAction={{
            label: t(
              "operations.loans_disbursement.suggestion.open_watch",
              "Open Article 90 Watch",
            ),
            onClick: () =>
              window.location.assign("/operations/loans/article-90-watch"),
          }}
          suggestionKey="disbursement-mutabaa-veto"
        />

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {filtered.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">
                    <input
                      type="checkbox"
                      checked={
                        filtered.length > 0 &&
                        filtered.every((r) => selected.has(r.application_id))
                      }
                      onChange={(e) =>
                        setSelected(
                          e.target.checked
                            ? new Set(filtered.map((r) => r.application_id))
                            : new Set(),
                        )
                      }
                    />
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.application", "Application")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.employee", "Employee")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.product", "Product")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.amount", "Approved (SAR)")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.tenure", "Tenure")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.approved_at", "Approved at")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.loans_disbursement.col.waiting", "Days waiting")}
                  </th>
                  <th className="px-3 py-2">
                    {t("common.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.application_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(row.application_id)}
                        onChange={() => toggle(row.application_id)}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/operations/loans/applications/${row.application_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.application_id.slice(0, 8)}…
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="text-text-secondary hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.product_name_en}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_disbursement.trace.amount",
                              `approved_amount = min(engine ceiling from loan_eligibility_max_amount rule, product.max_amount). Mutaba'aa enforces Article 90 at disbursement.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr" className="font-medium text-text-primary">
                          {formatSar(row.approved_amount_sar)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      {row.tenure_months} mo
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_disbursement.trace.approved_at",
                              `Approved at ${row.approved_at}. Approval workflow_event captures actor + chain step.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{row.approved_at.slice(0, 10)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.loans_disbursement.trace.waiting",
                              `days_waiting = today − approved_at. SLA is ${SLA_DAYS} days; over-threshold rows go rose.`,
                            ),
                          )
                        }
                      >
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            row.days_waiting > SLA_DAYS
                              ? severityChip("critical")
                              : severityChip("neutral")
                          }`}
                        >
                          {row.days_waiting}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/operations/loans/applications/${row.application_id}`}
                        className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                      >
                        {t("common.open", "Open")}
                        <ChevronRight className="ms-0.5 inline h-3 w-3" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {t(
              "operations.loans_disbursement.empty",
              "All approved loans disbursed — queue clear.",
            )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: stats.total,
            attention: stats.overdue,
            critical: stats.overdue,
            top_kind: t("operations.loans_disbursement.title", "Loan Disbursement Queue"),
            window_label: "approved",
          }}
        />
      </aside>
    </div>
  );
}
